import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { SharedComponents } from "../../../../components/shared.module";
import { ConfigService } from "../../../../core/services/config/config.service";
import { ExcelExportService } from "../../../../core/services/download-excel/excel-export.service";
import { RestTcoService } from "../../../../core/services/rest-tco.service";
import { TcoProjectService } from "../../../../core/services/tco-project/tco-project.service";
import { UtilitiesService } from "../../../../core/services/utilities.service";
import { MaterialModule } from "../../../../core/shared/material.module";
import { DuplicateTestCasesComponent } from "./duplicate-test-cases/duplicate-test-cases.component";
import { PotentialOverlapTestCasesComponent } from "./potential-overlap-test-cases/potential-overlap-test-cases.component";
import { UniqueTestCasesComponent } from "./unique-test-cases/unique-test-cases.component";

/**
 *
 * This component is responsible for displaying the results of an optimization process,
 * including information about test cases, duplicates, overlaps, and unique cases.
 * It provides options to toggle and view details for each type of test case result.
 */
@Component({
  selector: "app-optimizer-results",
  standalone: true,
  imports: [
    SharedComponents,
    CommonModule,
    MaterialModule,
    DuplicateTestCasesComponent,
    PotentialOverlapTestCasesComponent,
    UniqueTestCasesComponent,
  ],
  templateUrl: "./optimizer-results.component.html",
  styleUrl: "./optimizer-results.component.scss",
})
export class OptimizerResultsComponent implements OnInit {
  @Input() data: any = {};

  showGrandUnique: boolean = false;
  showDuplicated: boolean = false;
  showOverlap: boolean = false;
  selectedItem: any;
  topCardList: any = [];
  parentCard: any;
  middleCardList: any = [];
  chartData: any = [];
  optimizeChartLegend: any = [];
  uniqueChartLegend: any = [];
  percentageChartLegend: any;
  uniqueTestCasesFromDuplicatesCount: any = 0;
  uniqueTestCasesFromDuplicatesList: any = [];
  uniqueTestCases: any = [];
  obsoleteTestCases: any = [];
  chartOptions: any = {
    isLegendVisible: false,
  };
  modelId: any = "";
  optimizedPercentage: any;
  optimizedTestCaseCount: any;
  nonOptimizedTestCaseCount: any;
  result: any;
  duplicateCard: any;
  isLoading: boolean = false;
  overlappingTestCases: any = [];
  extractedRealm: string | null;
  currentAccountBusiness: any;
  currentAccountRole: any;
  duplicateTestcasesList: any;
  countOfUniquesInOverlapping: any;
  segmentData: any;
  overlapTestcasesList: any;
  uniqueTestcasesList: any;
  overlapCombinationTestcasesList: any;
  obsoleteTestcasesList: any;
  modelExecutionData: any;
  chartMeta: any = {
    showLabels : true
  };
  isMoreDetails = false;
  viewTypeMoreDetails = "unique_test_cases";
  moreDetailsItems = [
    {
      value: "unique",
      label: "Unique Test Cases",
    },
    {
      value: "overlapping",
      label: "Potential Overlap Test Cases",
    },
    {
      value: "duplicate",
      label: "Duplicate Test Cases",
    },
  ];
  isObsolete:boolean=false;
  totalUniqueTestCount:any = 0;
  totalUniqueTestCountWhenObsolete:any = 0;
  totalCountWithObsolete:any = 0;

  constructor(
    private restTcoService: RestTcoService,
    private excelService: ExcelExportService,
    private tcoProjectService: TcoProjectService,
    private configService: ConfigService,
    private utilityService: UtilitiesService
  ) {
    this.isLoading = true;
    this.tcoProjectService.getModalId().subscribe((id) => {
      this.isLoading = false;
      this.modelId = id;
    });
    this.extractedRealm = this.configService.getConfig("isDomainResolver")
      ? this.utilityService?.extractRealmFromUrl()
      : sessionStorage.getItem("selectedRealm");
  }
  /**
   * Lifecycle hook that initializes the component.
   * Sets up the mock data for the optimization results.
   *
   * @returns {void}
   */

  ngOnInit() {
    this.isMoreDetails = false;
    this.updateResults();
    this.getAccountDetails();

  }

  updateResults() {
    if (this.modelId) {
      this.getAllInformationResults();
    } else {
      this.getCurrentResults();
    }
  }

  getCurrentResults() {
    this.isLoading = true;
    this.restTcoService.getAllModelExecutions().subscribe(
      (response) => {
        this.isLoading = false;

        this.modelExecutionData = response;
        if (this.modelExecutionData.length > 0) {
          const lastObject =
            this.modelExecutionData[this.modelExecutionData.length - 1];
          this.modelId = lastObject.id;
          this.getAllInformationResults();
        }
      },
      (error) => {
        this.isLoading = false;
      }
    );
  }

  getAllInformationResults() {
    if (this.modelId) {
      this.getResultsById();
      this.getCountDuplicateTestcase();
      this.getSegmentationTestcases();
      this.getUniqueTestcases();
      this.getOverlapCombinationTestcases();
      this.getObsoleteTestCases();
      this.getDuplicateTestcases();
    }
  }

  getAccountDetails() {
    this.restTcoService.getAllAccounts().subscribe((res) => {
      let accounts = res.data;
      let currentAccount: any = {};
      let currentGroup: any = {};
      if (accounts.length) {
        currentAccount = accounts[accounts.length - 1];
        this.currentAccountBusiness = currentAccount.businessUnit;
        if (currentAccount.groups && currentAccount.groups.length) {
          currentGroup =
            currentAccount.groups[currentAccount.groups.length - 1];
          this.currentAccountRole = currentGroup.role;
        } else {
          this.currentAccountRole = "SME";
        }
      } else {
        this.currentAccountBusiness = "BFS";
      }
    });
  }

  getCountDuplicateTestcase() {
    this.isLoading = true;
    this.restTcoService
      .getCountUniqueInDuplicatesByModelId(this.modelId)
      .subscribe((response) => {
        this.isLoading = false;
        this.uniqueTestCasesFromDuplicatesList = response;
        this.uniqueTestCasesFromDuplicatesCount = response?.length;
        this.getOptimizedPercentage();
        this.reformatData();
      });
  }

  getSegmentationTestcases() {
    this.restTcoService
      .getAllSegmentationDataByModelId(this.modelId)
      .subscribe((response) => {
        this.segmentData = response;
        this.getOverlapTestcases();
      });
  }

  getOverlapTestcases() {
    this.restTcoService
      .getAllTestcasesByModelId(this.modelId)
      .subscribe((response) => {
        const testIds = response
          .map((item: any) =>
            item.testId
              .replace(/\[|\]/g, "")
              .split(",")
              .map((id: string) => id.trim())
          )
          .flat();
        const filteredSegments = this.segmentData.filter((segment: any) =>
          testIds.includes(segment.testId.trim())
        );
        this.overlapTestcasesList = filteredSegments;
      });
  }

  getUniqueTestcases() {
    this.restTcoService
      .getAllTestcasesByModel(this.modelId)
      .subscribe((response) => {
        this.uniqueTestcasesList = response;
      });
  }

  getOverlapCombinationTestcases() {
    this.restTcoService
      .getAllOverlapTestcasesByModelId(this.modelId)
      .subscribe((response) => {
        this.overlapCombinationTestcasesList = response;
      });
  }

  getResultsById() {
    this.isLoading = true;
    this.restTcoService.getResultsModelById(this.modelId).subscribe(
      (res) => {
        this.isLoading = false;
        this.result = res;
      },
      (error) => {
        this.isLoading = false;
      }
    );
  }

  ///// old
  reformatData() {
    this.optimizedTestCaseCount =  parseInt(this.result?.uniqueTestCasesCount) +
    parseInt(this.result?.mergeableTestCasesCount) +
    this.uniqueTestCasesFromDuplicatesCount;

    this.totalUniqueTestCount = parseInt(this.result?.uniqueTestCasesCount) + parseInt(this.uniqueTestCasesFromDuplicatesCount);

    this.totalUniqueTestCountWhenObsolete = this.totalUniqueTestCount + parseInt(this.result?.mergeableTestCasesCount);
    this.totalCountWithObsolete = parseInt(this.result?.totalTestCasesCount) +parseInt(this.result?.obsoleteTestCasesCount)
    
    this.duplicateCard = {
      duplicateTotal: this.result?.duplicateTestcaseCount,
      uniqueInduplicate: {
        type: "unique-from-duplicates",
        name: "Distinct Test Cases From Duplicates",
        isDownload: this.uniqueTestCasesFromDuplicatesCount == 0 ? false : true,
        value: this.uniqueTestCasesFromDuplicatesCount
          ? this.uniqueTestCasesFromDuplicatesCount
          : 0,
      },
    };

    this.parentCard = {
      type: "total",
      name: "Total Test Cases",
      value:
        this.result?.obsoleteTestCasesCount === 0
          ? this.result?.totalTestCasesCount
          : parseInt(this.result?.totalTestCasesCount) +
            parseInt(this.result?.obsoleteTestCasesCount),
    };

    this.topCardList = [
      {
        type: "stable",
        value: this.result?.totalTestCasesCount,
        name: "Active Test Cases",
        info: "Stable Test Cases",
      },
      {
        type: "obsolete",
        value: this.result?.obsoleteTestCasesCount,
        name: "Obsolete Test Cases",
        info: "Obsolete Test Cases",
        isDownload: this.result?.obsoleteTestCasesCount == 0 ? false :true,
      },
    ];

    this.middleCardList = [
      {
        type: "unique",
        value: this.result?.uniqueTestCasesCount,
        name: "Unique Test Cases",
        isDownload: this.result?.uniqueTestCasesCount == 0 ? false : true,
        isExpand: true
      },
      {
        type: "overlapping",
        value: this.result?.mergeableTestCasesCount,
        isDownload:this.result?.mergeableTestCasesCount == 0 ? false : true,
        name: "Potential Overlap Test Cases",
        isExpand: true
      },
    ];

    this.chartData = [
      {
        metricDisplayName: "Total Test Cases",
        info: "Total Unique Test Cases",
        value:this.totalUniqueTestCount,
        metricType: "pie", 
        bgColors: ["#2F76FF", "#51F2B8", "#00AAFF"],
        type: "total-unique",
        data: [
          { label: "Unique Test Cases", value: parseInt(this.result?.uniqueTestCasesCount) },
          { label: "Duplicate Test Cases", value: parseInt(this.uniqueTestCasesFromDuplicatesCount) },
          { label: "Overlap Test Cases", value: parseInt(this.result?.mergeableTestCasesCount) }
        ],
        summary: {
          type: "total-unique",
          name: "Total Unique Test Cases",
          isDownload: this.totalUniqueTestCount == 0 ? false : true,
          value: this.totalUniqueTestCount,
        },
      },
      {
        metricDisplayName: "Optimized Test Set",
        info: "Total Optimized Test Cases",
        metricType: "pie",
        type: "total-optimized",
        bgColors: ["#2F76FF", "#00AAFF", "#51F2B8"],
        value: this.optimizedTestCaseCount,
        showValueOnly: true,
         data: [
          { label: "Unique Test Cases", value: parseInt(this.result?.uniqueTestCasesCount) },
          { label: "Overlap Test Cases", value: parseInt(this.result?.mergeableTestCasesCount) },
          { label: "Distinct Test Cases From Duplicates", value: parseInt(this.uniqueTestCasesFromDuplicatesCount) },
        ],
        summary: {
          type: "total-optimized",
          name: "Total Optimized Test Cases",
          isDownload: this.optimizedTestCaseCount == 0 ? false : true,
          value: this.optimizedTestCaseCount
        }
      },           
      {
        metricDisplayName: "Optimization Percentage",
        info: "Optimization Percentage",
        bgColors: ["#5E1DE4", "#AFADAB"],
        value: this.optimizedPercentage,
        metricType: "doughnut", 
        data: [
          { label: "Optimized Test Cases", value: this.optimizedTestCaseCount },
          { label: "Unique Test Cases", value: parseInt(this.result?.totalTestCasesCount) - this.optimizedTestCaseCount }
        ]
      },      
    ];
  }

  getTileClass(size?: string): string {
    return size || "col-md-6"; // Default to full width if no size is provided
  }

  trackByTitle(index: number, item: any) {
    return item.title;
  }

  getObsoleteTestCases() {
    this.isLoading = true;
    this.restTcoService.getObsoleteTestCases(this.modelId).subscribe(
      (response) => {
        this.isLoading = false;
        this.obsoleteTestCases = response;
      },
      (error) => {
        this.isLoading = false;
      }
    );
  }

  regularOptimizedPercentage() {
    if (this.result?.totalTestCasesCount) {
      const totalTestCases = parseInt(this.result.totalTestCasesCount);
      const uniqueTestCases = parseInt(this.result.uniqueTestCasesCount);
      const uniqueInDuplicates = parseInt(
        this.uniqueTestCasesFromDuplicatesCount
      );

      const uniqueTestCasesCount = uniqueInDuplicates + uniqueTestCases;
      this.optimizedPercentage = parseFloat(
        (100 - (uniqueTestCasesCount / totalTestCases) * 100).toFixed(1)
      );
    } else {
      this.optimizedPercentage = "0.0";
    }
  }

  obsoleteOptimizedPercentage() {
    const totalTestcases =
      parseInt(this.result?.totalTestCasesCount) +
      parseInt(this.result?.obsoleteTestCasesCount);
    const uniqueTestCases = parseInt(this.result?.uniqueTestCasesCount);
    const uniqueInDuplicates = parseInt(
      this.uniqueTestCasesFromDuplicatesCount
    );
    const obsolteTestCases = parseInt(this.result?.mergeableTestCasesCount);
    const totalOptimizedTestcases =
      uniqueTestCases + uniqueInDuplicates + obsolteTestCases;
    this.optimizedPercentage = parseFloat(
      (100 - (totalOptimizedTestcases / totalTestcases) * 100).toFixed(1)
    );
  }

  getOptimizedPercentage() {
    if (parseInt(this.result?.obsoleteTestCasesCount) === 0) {
      this.isObsolete = false;
      this.regularOptimizedPercentage();

    } else {
      this.isObsolete = true;
      this.obsoleteOptimizedPercentage();
    }

    // const percentage = this.result.totalTestCasesCount -
    this.percentageChartLegend =
      parseInt(this.result?.obsoleteTestCasesCount) === 0
        ? `A repository containing ${
            this.result?.totalTestCasesCount
          } test cases have been optimized by ${
            this.optimizedPercentage
          }%, with the identification of ${this.totalUniqueTestCount
          } unique test cases`
        : `A repository containing ${ this.totalCountWithObsolete} test cases have been optimized by ${
            this.optimizedPercentage
          }%, with the identification of ${this.totalUniqueTestCountWhenObsolete
          } actionable test cases`;
  }

  downloadExcel(type: any) {
    switch (type) {
      case "unique":
        this.excelService.exportToExcel(
          this.uniqueTestcasesList,
          "Unique_Test_Cases"
        );

        break;
      case "total-optimized":
        var totalOptimized = [
          ...this.uniqueTestcasesList,
          ...this.overlapTestcasesList,
          ...this.uniqueTestCasesFromDuplicatesList,
        ];
        //unique, overlap, unique in dupli
        this.excelService.exportToExcel(
          totalOptimized,
          "Total_Optimized_Test_Cases"
        );
        break;
      case "total-unique":
        var totalUnique = [
          ...this.uniqueTestCases,
          ...this.uniqueTestCasesFromDuplicatesList,
        ];
        this.excelService.exportToExcel(totalUnique, "Total_Unique_Test_Cases");
        break;
      case "unique-from-duplicates":
        this.excelService.exportToExcel(
          this.uniqueTestCasesFromDuplicatesList,
          "Unique_Test_Cases_In_Duplicates"
        );
        break;
      case "stable":
        break;
      case "obsolete":
        this.excelService.exportToExcel(
          this.obsoleteTestCases,
          "Obsolete_Test_Cases"
        );
        break;
      case "overlapping":
        this.excelService.exportToExcel(
          this.overlapCombinationTestcasesList,
          "Overlapping_Test_Cases"
        );
        break;
      case "total":
        //obsolete+stable
        var total = [...this.obsoleteTestcasesList];
        this.excelService.exportToExcel(total, "Total_Test_Cases");
        break;
        case "duplicate":
          this.excelService.exportToExcel(
            this.duplicateTestcasesList,
            "Duplicate_Test_Cases"
          );
          break;        
        default:
        break;
    }
  }
  getDuplicateTestcases() {
   this.restTcoService.getAllDuplicatesTestcasesByModelId(this.modelId).subscribe(
     (response) => {
       this.duplicateTestcasesList = response;
     },
     (error) => {
       console.error("Error fetching duplicate test cases", error);
     }
   );
  }

get uniqueFromDuplicateIds(): string[] {
  return (this.uniqueTestCasesFromDuplicatesList as { testId: string }[]).map(tc => tc.testId?.toString());
}
  onExpand(type:any){
    this.isMoreDetails = true;
    this.viewTypeMoreDetails = type;
  }

  changeViewType() {
    
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"]) {
      this.initializeData();
    }
  }

  initializeData() {
    if (this.chartData?.[0]?.data) {
      const total0 = this.chartData[0].data.reduce((sum: number, item: any) => sum + item.value, 0);
      this.chartData[0].total = total0;
    }
  
    if (this.chartData?.[2]?.data) {
      const total2 = this.chartData[2].data.reduce((sum: number, item: any) => sum + item.value, 0);
      this.chartData[2].total = total2;
    }
    
  }

  calculatePercentage(value: number, chartIndex: number): number {
    const total = this.chartData[chartIndex]?.data?.reduce(
      (sum: number, item: any) => sum + item.value,
      0
    );
    return total ? (value / total) * 100 : 0;
  }
  handleBackFromDetails() {
    this.isMoreDetails = false;
  }
}
